import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, Download, FileText, Calendar, User, BarChart3, Eye, EyeOff } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '@shared/components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';
import { generateReportPDF } from '../utils/pdfGenerator';

import ExecutiveSummary from '../components/dashboards/ExecutiveSummary';
import SkillsRadarChart from '../components/charts/SkillsRadarChart';
import SkillsBarChart from '../components/charts/SkillsBarChart';
import CompensationChart from '../components/charts/CompensationChart';
import SkillGapChart from '../components/charts/SkillGapChart';
import CareerTimeline from '../components/dashboards/CareerTimeline';
import TechnicalSkillsMatrix from '../components/charts/TechnicalSkillsMatrix';
import LeadershipCompetencies from '../components/charts/LeadershipCompetencies';
import SoftSkillsAssessment from '../components/charts/SoftSkillsAssessment';
import UniqueDifferentiators from '../components/charts/UniqueDifferentiators';

interface ReportData {
  id: string;
  title: string;
  status: string;
  content: {
    sections: Array<{
      title: string;
      content: string;
      type: string;
    }>;
    summary: string;
    metadata: {
      generated_at: string;
      model_used: string;
      usage?: any;
    };
  };
  created_at: string;
  updated_at: string;
}

interface VisualizationData {
  executive_metrics?: {
    years_experience?: number;
    team_size?: string;
    industries?: string[];
    budget_managed?: string;
    cost_savings?: string;
    revenue_growth?: string;
    client_retention?: string;
    key_achievements?: string[];
  };
  skills_scores?: {
    technical?: { [key: string]: number };
    leadership?: { [key: string]: number };
  };
  technical_skills_matrix?: Array<{
    name: string;
    proficiency: number;
    category: string;
    yearsExperience?: number;
    lastUsed?: string;
  }>;
  leadership_competencies?: Array<{
    name: string;
    score: number;
    description?: string;
    examples?: string[];
  }>;
  soft_skills?: Array<{
    name: string;
    score: number;
    description?: string;
    impact?: string;
  }>;
  unique_differentiators?: Array<{
    title: string;
    description: string;
    impact?: string;
    category?: 'expertise' | 'achievement' | 'innovation' | 'leadership' | 'certification' | 'other';
    priority?: 'high' | 'medium' | 'low';
  }>;
  compensation_data?: {
    current_range: { min: number; max: number };
    market_range: { min: number; max: number };
    target_range: { min: number; max: number };
    percentile_current?: number;
    percentile_target?: number;
  };
  skill_gaps?: Array<{
    skill: string;
    current_level: number;
    required_level: number;
    gap_percentage: number;
    priority: 'high' | 'medium' | 'low';
    months_to_close?: number;
  }>;
  career_progression?: {
    milestones?: Array<{
      year: number | string;
      title: string;
      company?: string;
      achievement?: string;
      type?: 'promotion' | 'transition' | 'achievement';
    }>;
    future_pathways?: Array<{
      role: string;
      timeline: string;
      probability?: number;
      salary_range?: { min: number; max: number };
      requirements?: string[];
    }>;
  };
}

const ReportViewEnhanced: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user, isAdmin } = useAuth();
  const [report, setReport] = useState<ReportData | null>(null);
  const [visualizationData, setVisualizationData] = useState<VisualizationData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showTextReport, setShowTextReport] = useState(false);
  const [userName, setUserName] = useState<string>('');
  const [vizPollingAttempts, setVizPollingAttempts] = useState(0);
  const [vizExtractionStatus, setVizExtractionStatus] = useState<'pending' | 'processing' | 'completed' | 'failed'>('pending');
  const [isRetryingExtraction, setIsRetryingExtraction] = useState(false);

  useEffect(() => {
    const fetchReportAndVisualizations = async () => {
      if (!user || !id) return;

      setIsLoading(true);

      try {
        let query = supabase
          .from('reports')
          .select('*')
          .eq('id', id);

        if (!isAdmin) {
          query = query.eq('user_id', user.id);
        }

        const { data: reportData, error: reportError } = await query.maybeSingle();

        if (reportError) throw reportError;

        if (!reportData) {
          toast.error('Report not found');
          return;
        }

        if (reportData.status !== 'completed') {
          toast.error(`Report status is "${reportData.status}". Only completed reports can be viewed.`);
          return;
        }

        setReport(reportData);

        const { data: profileData } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', reportData.user_id)
          .maybeSingle();

        if (profileData?.full_name) {
          setUserName(profileData.full_name);
        }

        const { data: vizData, error: vizError } = await supabase
          .from('report_visualizations')
          .select('visualization_data, extraction_status')
          .eq('report_id', id)
          .maybeSingle();

        if (!vizError && vizData?.visualization_data) {
          setVisualizationData(vizData.visualization_data as VisualizationData);
          setVizExtractionStatus('completed');
          console.log('✅ Loaded real visualization data from database');
          console.log('📊 Visualization data keys:', Object.keys(vizData.visualization_data));
          console.log('📊 Executive metrics:', vizData.visualization_data.executive_metrics);
          console.log('📊 Skills scores:', vizData.visualization_data.skills_scores);
          console.log('📊 Full data:', vizData.visualization_data);
        } else if (vizData?.extraction_status) {
          setVizExtractionStatus(vizData.extraction_status as any);
          console.log(`ℹ️ Visualization extraction status: ${vizData.extraction_status}`);
        } else {
          console.log('ℹ️ No visualization data found - extraction may still be in progress');
          setVizExtractionStatus('pending');
          setVisualizationData(null);
        }
      } catch (error) {
        console.error('Error fetching report:', error);
        toast.error('Failed to fetch report');
      } finally {
        setIsLoading(false);
      }
    };

    fetchReportAndVisualizations();
  }, [id, user?.id, isAdmin]);

  useEffect(() => {
    if (!report || visualizationData || vizPollingAttempts >= 12) return;

    const pollForVisualizations = setInterval(async () => {
      console.log(`🔄 Polling for visualization data (attempt ${vizPollingAttempts + 1}/12)...`);

      const { data: vizData, error: vizError } = await supabase
        .from('report_visualizations')
        .select('visualization_data, extraction_status')
        .eq('report_id', id)
        .maybeSingle();

      if (!vizError && vizData?.visualization_data) {
        setVisualizationData(vizData.visualization_data as VisualizationData);
        setVizExtractionStatus('completed');
        console.log('✅ Visualization data loaded via polling');
        console.log('📊 Polled visualization data:', vizData.visualization_data);
        clearInterval(pollForVisualizations);
      } else if (vizData?.extraction_status === 'failed') {
        setVizExtractionStatus('failed');
        console.log('❌ Visualization extraction failed');
        clearInterval(pollForVisualizations);
      } else if (vizData?.extraction_status === 'processing') {
        setVizExtractionStatus('processing');
      } else {
        setVizPollingAttempts(prev => prev + 1);
      }
    }, 5000);

    return () => clearInterval(pollForVisualizations);
  }, [report, visualizationData, vizPollingAttempts, id]);

  const hasVisualizationData = visualizationData && (
    visualizationData.executive_metrics ||
    visualizationData.skills_scores ||
    visualizationData.compensation_data ||
    visualizationData.skill_gaps ||
    visualizationData.career_progression
  );

  console.log('🔍 Visualization state:', {
    hasVisualizationData,
    visualizationData,
    showTextReport,
    vizExtractionStatus,
    vizPollingAttempts
  });

  const handleRetryVisualizationExtraction = async () => {
    if (!report || !user) return;

    setIsRetryingExtraction(true);
    setVizExtractionStatus('processing');
    toast.info('Retrying visualization extraction...');

    try {
      const { data, error } = await supabase.functions.invoke('extract-career-intelligence-data', {
        body: {
          userId: user.id,
          reportId: report.id
        }
      });

      if (error) {
        throw error;
      }

      if (data?.success) {
        toast.success('Visualization extraction started successfully');
        setVizPollingAttempts(0); // Reset polling counter
        setVizExtractionStatus('processing');
      } else {
        throw new Error(data?.error || 'Failed to start visualization extraction');
      }
    } catch (error: any) {
      console.error('Error retrying visualization extraction:', error);
      toast.error(`Failed to retry extraction: ${error.message}`);
      setVizExtractionStatus('failed');
    } finally {
      setIsRetryingExtraction(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!report) return;

    setIsDownloading(true);
    try {
      const pdfData = {
        id: report.id,
        title: report.title,
        created_at: report.created_at,
        status: report.status,
        sections: report.content.sections || [],
        summary: report.content.summary || '',
        aiModels: [report.content.metadata?.model_used].filter(Boolean),
        personasUsed: [],
        metadata: {
          totalPromptsProcessed: 1,
          totalPersonasInvolved: 0,
          totalModelsUsed: 1,
          processingTimestamp: report.content.metadata?.generated_at || report.created_at,
          sessionId: report.id
        }
      };

      generateReportPDF(pdfData);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading report...</p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Report not found</h2>
          <p className="text-muted-foreground mb-4">
            The report you're looking for doesn't exist or you don't have permission to view it.
          </p>
          <Link to="/reports">
            <Button>
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back to Reports
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-6">
          <Link to="/reports" className="flex items-center text-primary hover:text-primary/80 mb-4">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to Reports
          </Link>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{report.title}</h1>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  <span>Generated: {new Date(report.created_at).toLocaleDateString()}</span>
                </div>
                {report.content.metadata?.model_used && (
                  <div className="flex items-center gap-1">
                    <User className="h-4 w-4" />
                    <span>AI Model: {report.content.metadata.model_used}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowTextReport(!showTextReport)}
              >
                {showTextReport ? (
                  <>
                    <BarChart3 className="h-4 w-4 mr-2" />
                    View Visuals
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    View Text
                  </>
                )}
              </Button>
              <Button onClick={handleDownloadPDF} disabled={isDownloading}>
                {isDownloading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-r-transparent mr-2" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Visualization Extraction Status Banner */}
        {!showTextReport && !hasVisualizationData && vizExtractionStatus !== 'completed' && (
          <div className={`rounded-lg p-4 mb-6 ${
            vizExtractionStatus === 'failed' ? 'bg-amber-50 border border-amber-200' :
            vizExtractionStatus === 'processing' ? 'bg-blue-50 border border-blue-200' :
            'bg-gray-50 border border-gray-200'
          }`}>
            <div className="flex items-start gap-3">
              {vizExtractionStatus === 'processing' && (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-r-transparent mt-0.5" />
              )}
              {vizExtractionStatus === 'failed' && (
                <BarChart3 className="h-5 w-5 text-amber-600 mt-0.5" />
              )}
              {vizExtractionStatus === 'pending' && (
                <BarChart3 className="h-5 w-5 text-gray-600 mt-0.5" />
              )}
              <div className="flex-1">
                <h3 className={`font-semibold ${
                  vizExtractionStatus === 'failed' ? 'text-amber-900' :
                  vizExtractionStatus === 'processing' ? 'text-blue-900' :
                  'text-gray-900'
                }`}>
                  {vizExtractionStatus === 'processing' && 'Generating Interactive Visualizations'}
                  {vizExtractionStatus === 'failed' && 'Visualization Generation Incomplete'}
                  {vizExtractionStatus === 'pending' && 'Preparing Visualizations'}
                </h3>
                <p className={`text-sm mt-1 ${
                  vizExtractionStatus === 'failed' ? 'text-amber-700' :
                  vizExtractionStatus === 'processing' ? 'text-blue-700' :
                  'text-gray-700'
                }`}>
                  {vizExtractionStatus === 'processing' && (
                    'AI is extracting structured data from your report to create interactive charts and insights. This usually takes 30-60 seconds.'
                  )}
                  {vizExtractionStatus === 'failed' && (
                    'The visualization extraction did not complete successfully. Your full text report is available below, but interactive charts may not display.'
                  )}
                  {vizExtractionStatus === 'pending' && (
                    'Visualizations will appear shortly. Your report text is fully available below.'
                  )}
                </p>
                {vizExtractionStatus === 'failed' && (
                  <button
                    onClick={handleRetryVisualizationExtraction}
                    disabled={isRetryingExtraction}
                    className="mt-3 px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isRetryingExtraction ? 'Retrying...' : 'Retry Visualization Extraction'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {!showTextReport ? (
          <div className="space-y-6">

            {!hasVisualizationData && vizPollingAttempts < 10 && (
              <div className="bg-blue-50 rounded-xl border border-blue-200 p-6 mb-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-r-transparent"></div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">
                      Generating Interactive Visualizations
                    </h3>
                    <p className="text-sm text-blue-800 mb-3">
                      Your report text is ready! We're now extracting structured data to create interactive
                      charts and visual insights. This typically takes 1-2 minutes.
                    </p>
                    <p className="text-sm text-blue-700 mb-2">
                      The page will automatically update when visualizations are ready. You can also view the
                      full text report using the "View Text" button above.
                    </p>
                    <div className="mt-3 text-xs text-blue-600">
                      Polling attempt {vizPollingAttempts + 1} of 10...
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!hasVisualizationData && vizPollingAttempts >= 10 && (
              <div className="bg-amber-50 rounded-xl border border-amber-200 p-6 mb-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <BarChart3 className="h-6 w-6 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-amber-900 mb-2">
                      Visualizations Still Processing
                    </h3>
                    <p className="text-sm text-amber-800 mb-3">
                      The visualization extraction is taking longer than expected. Your full text report
                      is available using the "View Text" button above.
                    </p>
                    <p className="text-sm text-amber-700">
                      The visualizations may appear in a few more minutes. Try refreshing the page, or
                      contact support if this message persists.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {hasVisualizationData && (
              <div className="bg-green-50 rounded-xl border border-green-200 p-4 mb-6">
                <div className="flex items-start gap-3">
                  <BarChart3 className="h-5 w-5 text-green-600 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-green-900 mb-1">Interactive Visualizations Loaded</h3>
                    <p className="text-sm text-green-700">
                      Your career data has been analyzed and structured into interactive charts and insights below.
                      Scroll down to explore your executive metrics, skills assessment, compensation analysis, and career progression.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {visualizationData?.executive_metrics && (
              <ExecutiveSummary metrics={visualizationData.executive_metrics} userName={userName} />
            )}

            {visualizationData?.skills_scores && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {visualizationData.skills_scores.leadership && (
                  <SkillsRadarChart
                    skills={Object.entries(visualizationData.skills_scores.leadership).map(([skill, score]) => ({
                      skill,
                      score
                    }))}
                    title="Leadership Competencies"
                    color="#0891b2"
                  />
                )}
                {visualizationData.skills_scores.technical && (
                  <SkillsBarChart
                    skills={Object.entries(visualizationData.skills_scores.technical).map(([skill, score]) => ({
                      skill,
                      score
                    }))}
                    title="Technical Skills"
                  />
                )}
              </div>
            )}

            {visualizationData?.compensation_data && (
              <CompensationChart data={visualizationData.compensation_data} />
            )}

            {visualizationData?.skill_gaps && visualizationData.skill_gaps.length > 0 && (
              <SkillGapChart gaps={visualizationData.skill_gaps} />
            )}

            {visualizationData?.career_progression && (
              <CareerTimeline
                milestones={visualizationData.career_progression.milestones}
                futurePathways={visualizationData.career_progression.future_pathways}
              />
            )}

            {visualizationData?.technical_skills_matrix && visualizationData.technical_skills_matrix.length > 0 && (
              <TechnicalSkillsMatrix skills={visualizationData.technical_skills_matrix} />
            )}

            {visualizationData?.leadership_competencies && visualizationData.leadership_competencies.length > 0 && (
              <LeadershipCompetencies competencies={visualizationData.leadership_competencies} />
            )}

            {visualizationData?.soft_skills && visualizationData.soft_skills.length > 0 && (
              <SoftSkillsAssessment skills={visualizationData.soft_skills} />
            )}

            {visualizationData?.unique_differentiators && visualizationData.unique_differentiators.length > 0 && (
              <UniqueDifferentiators differentiators={visualizationData.unique_differentiators} />
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-8">
              {report.content.sections && report.content.sections.length > 0 ? (
                <>
                  {(() => {
                    const valuePropSection = report.content.sections.find(
                      s => s.title.toLowerCase().includes('value proposition') ||
                           s.title.toLowerCase().includes('overall value')
                    );
                    const elevatorSpeechSection = report.content.sections.find(
                      s => s.title.toLowerCase().includes('elevator speech')
                    );
                    const talkingPointsSection = report.content.sections.find(
                      s => s.title.toLowerCase().includes('talking points')
                    );

                    const specialSectionTitles = [
                      valuePropSection?.title,
                      elevatorSpeechSection?.title,
                      talkingPointsSection?.title
                    ].filter(Boolean);

                    const mainSections = report.content.sections.filter(
                      s => !specialSectionTitles.includes(s.title)
                    );

                    return (
                      <>
                        {mainSections.map((section, index) => (
                          <div key={index} className={index === 0 ? "mb-8" : "mb-8 last:mb-0"}>
                            <div className="mb-6 pb-3 border-b border-gray-100">
                              <h2 className="text-2xl font-bold text-gray-900">{section.title}</h2>
                            </div>
                            <div className="prose prose-lg max-w-none">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {section.content}
                              </ReactMarkdown>
                            </div>
                          </div>
                        ))}

                        {(valuePropSection || elevatorSpeechSection || talkingPointsSection) && (
                          <div className="mt-12 pt-8 border-t-2 border-gray-200">
                            <div className="mb-6">
                              <span className="inline-block px-3 py-1 text-xs font-semibold text-blue-700 bg-blue-100 rounded-full">
                                COMMUNICATION TOOLS
                              </span>
                            </div>

                            {valuePropSection && (
                              <div className="mb-10 bg-gradient-to-br from-blue-50 to-cyan-50 -mx-8 px-8 pt-8 pb-10 border-b-4 border-blue-500">
                                <div className="mb-4">
                                  <h2 className="text-3xl font-bold text-gray-900 mb-2">
                                    {userName ? `${userName}'s ` : ''}Value Proposition
                                  </h2>
                                  <p className="text-sm text-gray-600">
                                    A concise overview of unique strengths and market value
                                  </p>
                                </div>
                                <div className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-strong:text-gray-900 prose-li:text-gray-700">
                                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {valuePropSection.content}
                                  </ReactMarkdown>
                                </div>
                              </div>
                            )}

                            {elevatorSpeechSection && (
                              <div className="mb-8">
                                <div className="mb-6 pb-3 border-b border-gray-100">
                                  <h2 className="text-2xl font-bold text-gray-900">{elevatorSpeechSection.title}</h2>
                                </div>
                                <div className="prose prose-lg max-w-none">
                                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {elevatorSpeechSection.content}
                                  </ReactMarkdown>
                                </div>
                              </div>
                            )}

                            {talkingPointsSection && (
                              <div className="mb-8 last:mb-0">
                                <div className="mb-6 pb-3 border-b border-gray-100">
                                  <h2 className="text-2xl font-bold text-gray-900">{talkingPointsSection.title}</h2>
                                </div>
                                <div className="prose prose-lg max-w-none">
                                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {talkingPointsSection.content}
                                  </ReactMarkdown>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </>
              ) : (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No content available</h3>
                  <p className="text-muted-foreground">
                    This report doesn't have any content sections to display.
                  </p>
                </div>
              )}
            </div>

            <div className="px-8 py-6 bg-gray-50 border-t border-gray-200">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="text-sm text-gray-600">
                  <p>Generated by FutureLens AI-powered analysis</p>
                  {report.content.metadata?.generated_at && (
                    <p>Processing completed: {new Date(report.content.metadata.generated_at).toLocaleString()}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportViewEnhanced;
